import Dexie, { type EntityTable } from "dexie";
import type { SupportedLocale } from "@repo/core";
import { localizedDefaultCategories } from "../i18n/labels";
import { readStoredLocale } from "../i18n/translations";
import { buildDemoDataset } from "./demoData";
import { isDefaultCategoryId } from "../i18n/labels";
import type {
  Account,
  Category,
  Debt,
  NetWorthLog,
  SinkingFund,
  Transaction,
} from "./schema";

export class BudgetDB extends Dexie {
  categories!: EntityTable<Category, "id">;
  accounts!: EntityTable<Account, "id">;
  transactions!: EntityTable<Transaction, "id">;
  debts!: EntityTable<Debt, "id">;
  sinkingFunds!: EntityTable<SinkingFund, "id">;
  netWorthLogs!: EntityTable<NetWorthLog, "id">;

  constructor() {
    super("annual-budget-db");
    this.version(1).stores({
      categories: "id, type, group",
      accounts: "id, type",
      transactions: "id, date, type, categoryId, accountId, person",
      debts: "id",
      sinkingFunds: "id",
      netWorthLogs: "id, monthYear",
    });
    this.version(2).stores({
      categories: "id, type, group",
      accounts: "id, type",
      transactions: "id, date, type, categoryId, accountId, person, seriesId",
      debts: "id",
      sinkingFunds: "id",
      netWorthLogs: "id, monthYear",
    });
  }
}

export const db = new BudgetDB();

const CLEARED_FLAG = "annual-budget-user-cleared";

async function wipeAllTables(): Promise<void> {
  await db.categories.clear();
  await db.accounts.clear();
  await db.transactions.clear();
  await db.debts.clear();
  await db.sinkingFunds.clear();
  await db.netWorthLogs.clear();
}

/** Replace category table with the shipped default taxonomy (localized). */
export async function restoreDefaultCategories(
  locale: SupportedLocale = readStoredLocale()
): Promise<void> {
  const cats = localizedDefaultCategories(locale);
  await db.transaction("rw", db.categories, async () => {
    await db.categories.clear();
    await db.categories.bulkAdd(cats);
  });
}

/**
 * Update names of default categories to match locale.
 * Skips categories the user renamed (handled by caller via labels helper when displaying;
 * here we refresh any row whose id is default).
 */
export async function relocalizeDefaultCategoryNames(
  locale: SupportedLocale
): Promise<void> {
  const localized = localizedDefaultCategories(locale);
  const byId = new Map(localized.map((c) => [c.id, c]));
  const existing = await db.categories.toArray();
  await db.transaction("rw", db.categories, async () => {
    for (const cat of existing) {
      if (!isDefaultCategoryId(cat.id)) continue;
      const next = byId.get(cat.id);
      if (next && cat.name !== next.name) {
        await db.categories.put({ ...cat, name: next.name });
      }
    }
  });
}

/** Ensure default categories exist without loading demo transactions/accounts. */
export async function ensureDefaultCategoriesOnly(
  locale: SupportedLocale = readStoredLocale()
): Promise<void> {
  const exists = await db.categories.count();
  if (exists > 0) return;
  await db.categories.bulkAdd(localizedDefaultCategories(locale));
}

/**
 * Seeds demo data only when explicitly requested (`force: true`).
 * First visit / empty DB no longer auto-loads demo — use ensureDefaultCategoriesOnly.
 */
export async function seedBudgetData(opts?: {
  force?: boolean;
  wipeFirst?: boolean;
  locale?: SupportedLocale;
  /** Hosted demo: seed all 12 months of the current year */
  fullYear?: boolean;
}): Promise<void> {
  // Never silently fill the app with demo data
  if (!opts?.force) return;

  if (opts?.wipeFirst || opts?.force) {
    await wipeAllTables();
  }

  const locale = opts?.locale ?? readStoredLocale();
  const { categories, accounts, debts, sinkingFunds, transactions, netWorthLogs } =
    buildDemoDataset(locale, { fullYear: opts?.fullYear });

  await db.categories.bulkAdd(categories);
  await db.accounts.bulkAdd(accounts);
  await db.debts.bulkAdd(debts);
  await db.sinkingFunds.bulkAdd(sinkingFunds);
  await db.transactions.bulkAdd(transactions);
  await db.netWorthLogs.bulkAdd(netWorthLogs);
  localStorage.removeItem(CLEARED_FLAG);
}

/**
 * Clears personal data (transactions, accounts, debts, sinking funds, net-worth logs).
 * Restores default categories (localized) so Add Transaction stays usable.
 */
export async function clearAllData(
  locale: SupportedLocale = readStoredLocale()
): Promise<void> {
  const cats = localizedDefaultCategories(locale);
  await db.transaction(
    "rw",
    [
      db.categories,
      db.accounts,
      db.transactions,
      db.debts,
      db.sinkingFunds,
      db.netWorthLogs,
    ],
    async () => {
      await db.accounts.clear();
      await db.transactions.clear();
      await db.debts.clear();
      await db.sinkingFunds.clear();
      await db.netWorthLogs.clear();
      await db.categories.clear();
      await db.categories.bulkAdd(cats);
    }
  );
  localStorage.setItem(CLEARED_FLAG, "1");
}

export interface ClearAllDataSnapshot {
  categoryIds: string[];
  accountCount: number;
  transactionCount: number;
  debtCount: number;
  sinkingFundCount: number;
  netWorthLogCount: number;
}

export async function snapshotAfterClear(): Promise<ClearAllDataSnapshot> {
  const [cats, accounts, txs, debts, funds, logs] = await Promise.all([
    db.categories.toArray(),
    db.accounts.count(),
    db.transactions.count(),
    db.debts.count(),
    db.sinkingFunds.count(),
    db.netWorthLogs.count(),
  ]);
  return {
    categoryIds: cats.map((c) => c.id).sort(),
    accountCount: accounts,
    transactionCount: txs,
    debtCount: debts,
    sinkingFundCount: funds,
    netWorthLogCount: logs,
  };
}

export async function resetAndSeed(
  locale: SupportedLocale = readStoredLocale()
): Promise<void> {
  localStorage.removeItem(CLEARED_FLAG);
  await seedBudgetData({ force: true, wipeFirst: true, locale, fullYear: true });
}

export const isDemoMode =
  import.meta.env.VITE_DEMO_MODE === "true" ||
  (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("demo"));
